import memoize from 'lodash/memoize';
import type { Question } from '../../legacy-series-types';
import type { CompatibilitySlide } from '../../compatibility-types';
import { getCommonSlideProperty } from '../../forward-compatibility/get-common-slide-property';
import { InvalidProxySetError } from '../../utils/invalid-proxy-set-error';
import { getQuestionProperty } from '../../backward-compatibility/question-properties/get-question-property';
import { commonSlidePropertyKeys } from './constants';

type GetCommonSlideCompatibilityHandler = (
  slidePropertyKeys?: Array<string>,
  mapSlidePropertyToQuestionProperty?: typeof getCommonSlideProperty,
) => ProxyHandler<CompatibilitySlide | Question>;

export const getCommonSlideCompatibilityHandler =
  memoize<GetCommonSlideCompatibilityHandler>(
    (
      slidePropertyKeys = commonSlidePropertyKeys,
      mapSlidePropertyToQuestionProperty = getCommonSlideProperty,
    ) => ({
      get(
        target: CompatibilitySlide | Question,
        key:
          | keyof CompatibilitySlide
          | keyof Question
          | '__isProxy'
          | '__target'
          | 'isMigrated',
      ) {
        if (key === '__isProxy') {
          return true;
        }

        if (key === '__target') {
          return target;
        }
        const isTargetMigrated = Reflect.has(target, 'staticContent');

        if (key === 'isMigrated') {
          return isTargetMigrated;
        }

        if (slidePropertyKeys.includes(key)) {
          if (isTargetMigrated) {
            // data is already migrated, return the value directly
            // 'slidePublicKey' -> 'slidePublicKey'
            return target[key as keyof typeof target];
          }

          // map common slide properties to question properties
          // 'slidePublicKey' -> 'public_key'
          return mapSlidePropertyToQuestionProperty(
            target as Question,
            key as keyof CompatibilitySlide,
          );
        }

        if (isTargetMigrated) {
          // map common slide properties to question properties
          // 'public_key' -> 'slidePublicKey'
          return getQuestionProperty(target as CompatibilitySlide, key as any);
        }

        // data is not migrated yet, return the value directly
        // 'public_key' -> 'public_key'
        return (target as Question)[key as keyof Question];
      },
      set(_: never, key: string) {
        throw new InvalidProxySetError(key, 'slide');
      },
    }),
  );
